import { useState } from 'react';
import { parseConditions, collectConditions } from '../segment/conditionModel';
import { diffLabels } from './useTouchpointEditor';
import { useJourneyMutation, useProductOptions } from './journeyHooks';
import { useLabelOptions } from './touchpointHooks';

const EMPTY = { name: '', state: 'PAUSED', isRestartable: false, isRecurring: false, channels: [], frequency: '', startDate: '', time: '' };

const formFromJourney = (j) => {
  if (!j) return { ...EMPTY };
  const recurrence = j.recurrence_spec || {};
  return {
    name: j.name || '', state: j.state_enum || 'PAUSED',
    isRestartable: !!j.is_restartable, isRecurring: !!j.is_recurring, channels: j.channels || [],
    frequency: recurrence.frequency || '', startDate: recurrence.start_date || '', time: recurrence.time ? recurrence.time.substring(0, 5) : '',
  };
};
const labelsOf = (j) => (j && j.labels ? j.labels : []).map((l) => l.uuid);
const sameKey = (form, labels, segDraft) => JSON.stringify([form, [...labels].sort(), segDraft]);

// Returns true if the segment draft contains at least one complete criterion.
const segmentDraftHasCriteria = (draft) => collectConditions(draft.groups, draft.conditionOperator).condition != null;

export function buildJourneyBody(form) {
  // is_active is intentionally NOT set here — activation is a soft-delete concern handled
  // by a (future) delete affordance, not this config drawer. Create defaults active server-side.
  const body = {
    name: form.name, state_enum: form.state,
    is_restartable: form.isRestartable, is_recurring: form.isRecurring, channels: form.channels,
  };
  body.recurrence_spec = form.isRecurring
    ? { frequency: form.frequency || null, start_date: form.startDate || null, time: form.time || null }
    : {};
  return body;
}

export function buildSegmentBody({ groups, conditionOperator, existingSegment, journeyName }) {
  const { condition, condition_operator } = collectConditions(groups, conditionOperator);
  const body = { name: (existingSegment && existingSegment.name) || journeyName, condition: condition || [], condition_operator };
  if (existingSegment && existingSegment.parent_segment_id != null) body.parent_segment_id = existingSegment.parent_segment_id;
  return body;
}

export function useJourneyEditor({ open, mode, journey, journeyId }) {
  const isCreate = mode === 'create';
  const mutation = useJourneyMutation();
  const { data: labelOptions = [] } = useLabelOptions();
  const { data: productOptions = [] } = useProductOptions();

  const [form, setForm] = useState(EMPTY);
  const [labelUuids, setLabelUuids] = useState([]);
  const [segmentDraft, setSegmentDraft] = useState({ groups: parseConditions(null), conditionOperator: 'AND' });

  // Synchronous seed on open / target change (mirror useTouchpointEditor — avoids first-render flash).
  const [seededKey, setSeededKey] = useState(null);
  const openKey = open ? `${mode}|${journey ? journey.journey_id : 'new'}` : null;
  if (!open && seededKey !== null) {
    setSeededKey(null); // drawer closed → forget, so the next open always re-seeds
  } else if (open && openKey !== seededKey) {
    setSeededKey(openKey);
    setForm(formFromJourney(journey));
    setLabelUuids(labelsOf(journey));
    const seg = journey && journey.segment ? journey.segment : null;
    setSegmentDraft({ groups: parseConditions(seg && seg.condition), conditionOperator: (seg && seg.condition_operator) || 'AND' });
  }

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const canSave = form.name.trim() !== '';

  // Compute dirty: compare current form/labels/segment to the seed values.
  const seedSegDraft = (() => {
    const seg = journey && journey.segment ? journey.segment : null;
    return { groups: parseConditions(seg && seg.condition), conditionOperator: (seg && seg.condition_operator) || 'AND' };
  })();
  const seedKey = sameKey(formFromJourney(journey), labelsOf(journey), seedSegDraft);
  const dirty = sameKey(form, labelUuids, segmentDraft) !== seedKey;

  const previewJourney = (!isCreate && journey)
    ? { ...journey, name: form.name, state_enum: form.state, channels: form.channels }
    : null;

  const save = (onDone, onError) => {
    const existingSegment = journey && journey.segment ? journey.segment : null;
    // A new journey must always be linked to a segment (backend requires segment_id on
    // create). When no criteria are defined we still create an empty, journey-named
    // segment ("reaches everyone"). For configure, only touch the segment when it changed.
    const changed = isCreate || segmentDraftHasCriteria(segmentDraft) || (existingSegment != null);
    const vars = {
      isCreate, journeyId: isCreate ? null : journeyId,
      journeyBody: buildJourneyBody(form),
      segment: {
        changed,
        segmentId: journey ? journey.segment_id || null : null,
        body: buildSegmentBody({ ...segmentDraft, existingSegment, journeyName: form.name }),
      },
      labelDiff: diffLabels(labelsOf(journey), labelUuids),
    };
    mutation.mutate(vars, {
      onSuccess: (res) => onDone && onDone(res.journeyId),
      onError: (err) => onError && onError(err),
    });
  };

  return {
    values: form, set, labelUuids, setLabelUuids, labelOptions, productOptions,
    segmentDraft, setSegmentDraft, dirty, canSave, isCreate, previewJourney,
    saving: mutation.isLoading, save,
  };
}
